import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from '../localstorage.service';
import { UtilityService } from '../utility_functions/utility_functions.service';
import dayjs from 'dayjs';
import { Subject, Subscription } from 'rxjs';


@Injectable({
    providedIn: 'root'
})
export class LocalisationService
{
    supportLanguages = ['en'];
    currentLanguage: string = '';
    languageChanged: Subject<string> = new Subject<string>();

    private languageChangedSubscription: Subscription;
  
    /**
     * Constructor
     */
    constructor(private translateService: TranslateService,private localStorageService: LocalStorageService,private utilityService: UtilityService)
    {
      this.languageChangedSubscription = this.languageChanged.subscribe((lang: string) => {
        if (lang) {
          this.setDefaultLanguage(lang);
          this.utilityService.loadLocale();
        }
      });
    }

    setDefaultLanguage(lang?: string): void{
      const userLanguage = this.localStorageService.getLocalStorage('user_preferences')?.language;
      const isB2BConfigEnabled = this.utilityService.isCompanyB2BEnabled();
      const defaultLang = lang && lang.length > 0 ? lang : (userLanguage || 'en');
      this.currentLanguage = defaultLang;

      // Include the '-b2b' suffix if B2B config is enabled
      const selectedLanguage = isB2BConfigEnabled ? `${defaultLang}-b2b` : defaultLang;

      // Add lang to supported languages if provided and not already included
      if (lang && !this.supportLanguages.includes(lang)) {
        this.supportLanguages.push(lang);
      }

      // Prepare the language path and set default language
      const languagePath = this.utilityService.isEmptyOrUndefined(defaultLang) ? 'en' : defaultLang;
      const finalLang = `${languagePath?.toLowerCase()}/${selectedLanguage?.toLowerCase()}`;

      // Adding and setting language with translation service
      this.importDayJsLocaleFile(languagePath?.toLowerCase());
      this.translateService.addLangs(this.supportLanguages);
      this.translateService.setDefaultLang(finalLang);
      this.translateService.use(finalLang);
    }

    getCurrentLanguage(): string {
      return this.currentLanguage ?? 'en';
    }

    importDayJsLocaleFile(locale: string): void {
      import(`dayjs/locale/${locale}`).then(() => {
        dayjs.locale(locale);
      },
      () => { // in case the import fails
          // maybe default to english
         dayjs.locale('en');
      });
    }

    loadSelectedLanguage(lang: string): void {
      const isB2BConfigEnabled = this.utilityService.isCompanyB2BEnabled();
      lang = lang.toLowerCase();
      const selectedLanguage = isB2BConfigEnabled ? `${lang}-b2b` : lang;
      this.translateService.use(`${lang}/${selectedLanguage}`);
    }

    updateSelectedLanguage(lang: string): void {
      if (lang && lang !== this.currentLanguage) {
        this.languageChanged.next(lang);
      }
    }

    ngOnDestroy(): void {
      // Unsubscribe from the languageChanged Subject to prevent memory leaks
      this.languageChangedSubscription?.unsubscribe();
    }
}
